import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Mail, Phone, User, Edit, Trash2 } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { toast } from "@/hooks/use-toast";
import CreateContactModal from "@/components/modals/create-contact-modal";
import { insertContactSchema } from "@shared/schema";
import { z } from "zod";
import { formatPhoneNumber } from "@/lib/utils";

interface Contact {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  jobTitle: string;
  department: string;
  userId?: string;
  createdAt: string;
  updatedAt: string;
  user?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  relationshipType?: string;
  isPrimary?: boolean;
}

interface ContactsSectionProps {
  entityType: string;
  entityId: string;
  title?: string;
}

export default function ContactsSection({ entityType, entityId, title = "Contacts" }: ContactsSectionProps) {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const queryClient = useQueryClient();

  const { data: contacts = [], isLoading } = useQuery<Contact[]>({
    queryKey: ['/api/contacts', entityType, entityId],
    queryFn: async () => {
      if (entityType && entityId && entityId !== "all") {
        // Use junction table endpoint for companies
        if (entityType === "company") {
          const response = await apiRequest("GET", `/api/companies/${entityId}/contacts`);
          const data = await response.json();
          // Transform the response to extract contact objects from junction table format
          return data.map((item: any) => item.contact);
        }
        // Use query parameters for other entity types
        const response = await apiRequest("GET", `/api/contacts?entityType=${entityType}&entityId=${entityId}`);
        return response.json();
      }
      const response = await apiRequest("GET", "/api/contacts");
      return response.json();
    },
  });

  const createContactMutation = useMutation({
    mutationFn: async (contactData: z.infer<typeof insertContactSchema>) => {
      await apiRequest("POST", "/api/contacts", contactData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/contacts'] });
      setIsCreateModalOpen(false);
      toast({
        title: "Success",
        description: "Contact created successfully",
      });
    },
    onError: (error) => {
      console.error("Error creating contact:", error);
      toast({
        title: "Error",
        description: "Failed to create contact",
        variant: "destructive",
      });
    },
  });

  const deleteContactMutation = useMutation({
    mutationFn: async (contactId: number) => {
      await apiRequest("DELETE", `/api/contacts/${contactId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/contacts'] });
      toast({
        title: "Success",
        description: "Contact deleted successfully",
      });
    },
    onError: (error) => {
      console.error("Error deleting contact:", error);
      toast({
        title: "Error",
        description: "Failed to delete contact",
        variant: "destructive",
      });
    },
  });

  const handleCreateContact = (contactData: z.infer<typeof insertContactSchema>) => {
    createContactMutation.mutate(contactData);
  };

  const handleDeleteContact = (contactId: number) => {
    if (confirm("Are you sure you want to delete this contact?")) {
      deleteContactMutation.mutate(contactId);
    }
  };

  const getRoleBadgeColor = (role: string) => {
    const colors: Record<string, string> = {
      owner: "bg-amber-100 text-amber-800",
      billing: "bg-green-100 text-green-800",
      operations: "bg-blue-100 text-blue-800",
      dispatch: "bg-purple-100 text-purple-800",
      safety: "bg-orange-100 text-orange-800",
      compliance: "bg-indigo-100 text-indigo-800",
      emergency: "bg-red-100 text-red-800",
      other: "bg-gray-100 text-gray-800",
    };
    return colors[role] || colors.other;
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          <CardDescription>Loading contacts...</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>{title}</CardTitle>
            <CardDescription>
              {contacts.length} contact{contacts.length !== 1 ? 's' : ''}
            </CardDescription>
          </div>
          <Button onClick={() => setIsCreateModalOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Contact
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {contacts.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No contacts yet. Click "Add Contact" to create one.
          </div>
        ) : (
          <div className="space-y-4">
            {contacts.map((contact, index) => (
              <div key={contact.id}>
                <div className="flex items-start justify-between">
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-gray-500" />
                      <span className="font-medium">
                        {contact.firstName} {contact.lastName}
                      </span>
                      {contact.relationshipType && (
                        <Badge 
                          variant="secondary" 
                          className={getRoleBadgeColor(contact.relationshipType)}
                        >
                          {contact.relationshipType}
                        </Badge>
                      )}
                      {contact.isPrimary && (
                        <Badge variant="default" className="bg-green-600">
                          Primary Contact
                        </Badge>
                      )}
                    </div>
                    
                    {contact.jobTitle && (
                      <div className="text-sm text-gray-600">
                        {contact.jobTitle}
                        {contact.department && ` • ${contact.department}`}
                      </div>
                    )}
                    
                    <div className="flex items-center gap-4 text-sm text-gray-600">
                      {contact.email && (
                        <div className="flex items-center gap-1">
                          <Mail className="h-3 w-3" />
                          <a href={`mailto:${contact.email}`} className="hover:text-blue-600">
                            {contact.email}
                          </a>
                        </div>
                      )}
                      {contact.phone && (
                        <div className="flex items-center gap-1">
                          <Phone className="h-3 w-3" />
                          <a href={`tel:${contact.phone}`} className="hover:text-blue-600">
                            {formatPhoneNumber(contact.phone)}
                          </a>
                        </div>
                      )}
                    </div>
                    
                    {contact.user && (
                      <div className="text-xs text-blue-600">
                        Linked to app user: {contact.user.firstName} {contact.user.lastName}
                      </div>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleDeleteContact(contact.id)}
                      disabled={deleteContactMutation.isPending}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                
                {index < contacts.length - 1 && <Separator className="mt-4" />}
              </div>
            ))}
          </div>
        )}
      </CardContent>
      
      <CreateContactModal
        open={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreateContact}
        isLoading={createContactMutation.isPending}
        initialData={
          entityId !== "all" ? {
            entityType,
            entityId,
          } : undefined
        }
      />
    </Card>
  );
}